import { useFetchWidgetID } from "@faire/deprecated-retailer-visitor-shared/hooks/hCaptcha/useFetchWidgetId";
import {
  hCaptchaQueryKey,
  logHCaptchaError,
  timeBeforeRefetchMS,
} from "@faire/deprecated-retailer-visitor-shared/lib/captchaUtils";
import {
  getSettingExperimentalHCaptchaTimeoutMS,
  getSettingAndAssignEnableExperimentalLoginLatencyInterventions,
  getSettingHCaptchaTimeoutMS,
} from "@faire/deprecated-retailer-visitor-shared/settings/getSettingHCaptchaTimeoutMS";
import { getSettingSkipHCaptchaCallsForTrustworthySessions } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingSkipHCaptchaCallsForTrustworthySessions";
import { delay } from "@faire/typescript/delay";
import {
  trackSigninHcaptchaPrecheckInit,
  trackSigninHcaptchaPrecheckInitTraceDataRequested,
  trackSigninHcaptchaPrecheckInitTraceDataRetrieved,
  trackSigninHcaptchaPrecheckInitTraceRetrievalFailed,
} from "@faire/web-api--source/events/signin/actionUnknown/hcaptchaPrecheckInit";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { Tracking } from "./tracking";
import { useFetchHCaptchaData } from "./useFetchHCaptchaData";
import { usePostHCaptchaToBE } from "./usePostHCaptchaToBE";
import { usePrecheckSession } from "./usePrecheckSession";
import { usePrefetchToken } from "./usePrefetchToken";

/**
 * Prefetches hCaptcha token and returns fetchHCaptchaToken. Should be used ahead of calling
 * endpoints we want to protect with rate limiting. Races against timeout (value of
 * getSettingHCaptchaTimeoutMS()) to prevent degrading user experience too much. If timeout wins,
 * return an error.
 *
 * returns fetchHCaptchaToken(shouldPostTokenToBackend: boolean)
 * When called, returns prefetched value or kicks off a new fetch. If shouldPostTokenToBackend
 * is true, will also post the token to the backend – this might be desireable if we are not
 * including the token in another backend API request (e.g. the login request accepts an
 * hCaptcha token)
 */
export const useFetchHCaptchaToken = () => {
  const { postHCaptchaToBE } = usePostHCaptchaToBE();
  const { fetchTokenWithTimeout } = useFetchTokenWithTimeout();
  const { prefetchToken } = usePrefetchToken();

  void prefetchToken();

  const fetchHCaptchaToken = useCallback(
    async (
      shouldPostTokenToBackend: boolean
    ): Promise<ILoginRequest.IHCaptchaData | undefined> => {
      Tracking.start();

      const data = await fetchTokenWithTimeout();
      if (!data) {
        return;
      }

      Tracking.end(data);

      // we must await sending data to BE to avoid race conditions
      // with the requests made after this point (i.e. login)
      await postHCaptchaToBE(shouldPostTokenToBackend, data);

      return data;
    },
    [fetchTokenWithTimeout, postHCaptchaToBE]
  );

  return { fetchHCaptchaToken };
};

const useFetchTokenWithTimeout = () => {
  const { fetchWidgetID } = useFetchWidgetID();
  const queryClient = useQueryClient();
  const { fetchHCaptchaData } = useFetchHCaptchaData();
  const { fetch: fetchPrecheckSession } = usePrecheckSession();

  const fetchTokenWithTimeout = useCallback(async (): Promise<
    ILoginRequest.IHCaptchaData | undefined
  > => {
    try {
      trackSigninHcaptchaPrecheckInit();

      const sessionTrustworthinessCheckRace = Promise.race([
        fetchPrecheckSession(),
        hCaptchaTimeout<void>(),
      ]);

      trackSigninHcaptchaPrecheckInitTraceDataRequested();
      const precheckSession = await sessionTrustworthinessCheckRace.catch(
        () => {
          throw new Error(ILoginRequest.IHCaptchaData.HCaptchaError.TIMED_OUT);
        }
      );

      if (precheckSession?.isTrustworthy) {
        trackSigninHcaptchaPrecheckInitTraceDataRetrieved();
        return;
      }

      if (
        precheckSession?.error instanceof WebApiError &&
        precheckSession.error.httpStatusCode === 400
      ) {
        trackSigninHcaptchaPrecheckInitTraceRetrievalFailed();
      }

      const widgetID = await Promise.race([
        fetchWidgetID(),
        hCaptchaTimeout<string>(),
      ]);

      const promise = queryClient.fetchQuery({
        queryKey: hCaptchaQueryKey(widgetID),
        queryFn: () => fetchHCaptchaData(widgetID),
        staleTime: timeBeforeRefetchMS(),
        gcTime: timeBeforeRefetchMS(),
        networkMode: "offlineFirst",
      });

      const token = await Promise.race([
        promise,
        hCaptchaTimeout<ILoginRequest.IHCaptchaData>(),
      ]);
      return token;
    } catch (error: unknown) {
      return handleHCaptchaDataError(error);
    }
  }, [fetchWidgetID, queryClient, fetchHCaptchaData]);

  // Keep as a fallback to use if we disable the setting.
  // TODO: Remove after setting is no longer used.
  const originalFetchTokenWithTimeout =
    useCallback(async (): Promise<ILoginRequest.IHCaptchaData> => {
      try {
        const widgetID = await Promise.race([
          fetchWidgetID(),
          hCaptchaTimeout<string>(),
        ]);

        const promise = queryClient.fetchQuery({
          queryKey: hCaptchaQueryKey(widgetID),
          queryFn: () => fetchHCaptchaData(widgetID),
          staleTime: timeBeforeRefetchMS(),
          gcTime: timeBeforeRefetchMS(),
          networkMode: "offlineFirst",
        });

        const token = await Promise.race([
          promise,
          hCaptchaTimeout<ILoginRequest.IHCaptchaData>(),
        ]);

        return token;
      } catch (error: unknown) {
        return handleHCaptchaDataError(error);
      }
    }, [fetchWidgetID, queryClient, fetchHCaptchaData]);

  return {
    fetchTokenWithTimeout: getSettingSkipHCaptchaCallsForTrustworthySessions()
      ? fetchTokenWithTimeout
      : originalFetchTokenWithTimeout,
  };
};

const hCaptchaTimeout = async <T>(): Promise<T> => {
  const delayMS =
    getSettingAndAssignEnableExperimentalLoginLatencyInterventions()
      ? getSettingExperimentalHCaptchaTimeoutMS()
      : getSettingHCaptchaTimeoutMS();
  await delay(delayMS);
  throw new Error(ILoginRequest.IHCaptchaData.HCaptchaError.TIMED_OUT);
};

const handleHCaptchaDataError = (
  error: unknown
): ILoginRequest.IHCaptchaData => {
  const errorToLog: ILoginRequest.IHCaptchaData.HCaptchaError =
    typeof error === "object"
      ? ((error as Error).message as ILoginRequest.IHCaptchaData.HCaptchaError)
      : (error as ILoginRequest.IHCaptchaData.HCaptchaError);
  if (
    !Object.values(ILoginRequest.IHCaptchaData.HCaptchaError).includes(
      errorToLog
    )
  ) {
    logHCaptchaError(
      errorToLog,
      ILoginRequest.IHCaptchaData.HCaptchaError.H_CAPTCHA_ERROR_UNKNOWN
    );
    return ILoginRequest.IHCaptchaData.build({
      error: ILoginRequest.IHCaptchaData.HCaptchaError.H_CAPTCHA_ERROR_UNKNOWN,
    });
  }
  logHCaptchaError(error, errorToLog);

  return ILoginRequest.IHCaptchaData.build({
    error: errorToLog,
  });
};
